/**
 * « Mes inscriptions » : quelles semaines, pour quel enfant, et comment annuler.
 *
 * ## Le statut vient de l'inscription, plus du paiement
 *
 * Chaque ligne portait « Payé » ou « En attente », lu sur
 * `registrations.payment_status`. Cette colonne n'a pas suivi la reprise des
 * paiements — une facture est acquittée quand elle est rapprochée d'une
 * écriture bancaire — et sur les 3 715 factures de la base, 2 256 réglées
 * s'affichaient « En attente » et 28 impayées « Payé ». Deux tiers des lignes
 * disaient le contraire de la réalité.
 *
 * Ce n'était pas non plus la question posée. Un parent ouvre cette page pour
 * savoir si son enfant a sa place cette semaine-là. `lib/inscriptions/statut`
 * y répond en trois états lus sur l'inscription : validée, annulation
 * demandée, annulée.
 *
 * ## Groupé par enfant
 *
 * La page listait tout à plat, dans l'ordre des achats, avec un champ de
 * recherche et un filtre « Payés / En attente » pour s'y retrouver. Une
 * famille en a rarement plus d'une dizaine : les grouper par enfant et replier
 * les semaines passées rend les deux outils inutiles — et un outil de
 * recherche qu'on n'a pas besoin d'utiliser est surtout une chose de plus à
 * comprendre. Le filtre, de surcroît, filtrait sur la colonne fausse.
 *
 * ## Ce qui reste
 *
 * Le tarif, qui est exact — c'est ce qui a été facturé. La facture et la note
 * de crédit, quand elles existent. La liste d'attente. Et le seul geste que le
 * parent vient faire ici : demander une annulation.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { avecSessionsLisibles } from '../../lib/inscriptions/sessionLisible';
import {
  estPassee,
  periodeLisible,
  peutDemanderAnnulation,
  statutInscription,
  type StatutAnnulation,
} from '../../lib/inscriptions/statut';
import { useWaitingListStore } from '../../stores/waitingListStore';
import { useCartStore } from '../../stores/cartStore';
import { useRegistrationStore } from '../../stores/registrationStore';
import { useCancellationRequestStore } from '../../stores/cancellationRequestStore';
import { useAuthStore } from '../../stores/authStore';
import {
  ArrowLeft,
  CalendarDays,
  ChevronDown,
  Clock,
  FileText,
  Loader2,
  MapPin,
} from 'lucide-react';
import { CancellationRequestModal } from '../../components/modals/CancellationRequestModal';
import { PastilleInscription } from '../../components/accueil/Pastille';
import clsx from 'clsx';
import toast, { Toaster } from 'react-hot-toast';

interface Registration {
  id: string;
  amount_paid: number;
  created_at: string;
  kid_id: string;
  activity_id: string;
  price_type: string;
  invoice_url: string | null;
  cancellation_status: StatutAnnulation;
  /*
    Presente mais eventuellement nulle, et non facultative : la requete la
    demande toujours, et la modale d'annulation attend exactement cette forme.
  */
  kid: {
    prenom: string;
    nom: string;
  } | null;
  session: {
    /* `type_activite` : une journée pédagogique a sa propre règle d'annulation. */
    stage: { title: string; type_activite?: string | null };
    start_date: string;
    end_date: string;
    center: { name: string };
  };
  cancellation_request?: {
    id: string;
    status: string;
    refund_type: string | null;
    credit_note_id: string | null;
    credit_note_url: string | null;
  };
}

/** Le nom d'un enfant, quelle que soit la forme que prend la jointure. */
const nomEnfant = (reg: Registration) =>
  reg.kid ? `${reg.kid.prenom} ${reg.kid.nom}`.trim() : 'Enfant inconnu';

const Etiquette = ({ children, ton }: { children: React.ReactNode; ton: string }) => (
  <span className={clsx('inline-block rounded px-2 py-0.5 text-xs', ton)}>{children}</span>
);

const Lien = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
  >
    <FileText className="h-4 w-4" aria-hidden="true" />
    {children}
  </a>
);

const CarteSemaine = ({
  reg,
  onAnnuler,
}: {
  reg: Registration;
  onAnnuler: (reg: Registration) => void;
}) => {
  const statut = statutInscription(reg.cancellation_status);
  const noteDeCredit = reg.cancellation_request?.credit_note_url;
  /*
    Un stage termine ne s'annule plus. La borne est la FIN du stage, pas son
    debut : une annulation en cours de semaine reste legitime (maladie,
    accident), et c'est justement le cas ou une note de credit partielle a un
    sens. `estPassee` compare a la date de fin, et le bouton disparait apres.
  */
  const annulable = peutDemanderAnnulation(reg.cancellation_status) && !estPassee(reg.session.end_date);

  return (
    <li className="rounded-xl bg-white p-4 ring-1 ring-gray-200">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="min-w-0 font-medium text-gray-900">{reg.session.stage.title}</h4>
        <PastilleInscription statut={statut} />
      </div>

      <p className="mt-1.5 flex items-center gap-1.5 text-sm text-gray-600">
        <CalendarDays className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
        {periodeLisible(reg.session.start_date, reg.session.end_date)}
      </p>
      <p className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-600">
        <MapPin className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
        {reg.session.center.name}
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-900">{reg.amount_paid} €</span>
        {reg.price_type?.includes('reduced') && (
          <Etiquette ton="bg-green-100 text-green-800">Tarif réduit</Etiquette>
        )}
        {reg.price_type?.includes('local') && (
          <Etiquette ton="bg-blue-100 text-blue-800">Tarif local</Etiquette>
        )}
      </div>

      {statut.detail && <p className="mt-2 text-sm text-amber-800">{statut.detail}</p>}

      <div className="mt-3 flex flex-wrap gap-2">
        {annulable && (
          <button
            type="button"
            onClick={() => onAnnuler(reg)}
            className="inline-flex min-h-[44px] items-center rounded-lg border border-red-200 px-3 text-sm font-medium text-red-700 hover:bg-red-50"
          >
            Demander l’annulation
          </button>
        )}
        {reg.invoice_url && <Lien href={reg.invoice_url}>Facture</Lien>}
        {noteDeCredit && <Lien href={noteDeCredit}>Note de crédit</Lien>}
      </div>
    </li>
  );
};

const GroupeEnfant = ({
  prenom,
  aVenir,
  passees,
  onAnnuler,
}: {
  prenom: string;
  aVenir: Registration[];
  passees: Registration[];
  onAnnuler: (reg: Registration) => void;
}) => {
  const [historiqueOuvert, setHistoriqueOuvert] = useState(false);

  return (
    <section className="mt-6">
      {/*
        La taille est posee explicitement : `index.css` donne aux `h2` un
        `text-2xl md:text-3xl` global, qui ferait du prenom d'un enfant le plus
        gros mot de la page, devant son titre.
      */}
      <h2 className="mb-3 text-lg font-semibold text-gray-900">{prenom}</h2>

      {aVenir.length === 0 ? (
        <p className="text-sm text-gray-500">Aucune semaine à venir.</p>
      ) : (
        <ul className="space-y-3">
          {aVenir.map((reg) => (
            <CarteSemaine key={reg.id} reg={reg} onAnnuler={onAnnuler} />
          ))}
        </ul>
      )}

      {passees.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setHistoriqueOuvert((v) => !v)}
            aria-expanded={historiqueOuvert}
            className="mt-3 flex min-h-[44px] items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <ChevronDown
              className={clsx('h-4 w-4 transition-transform', historiqueOuvert && 'rotate-180')}
              aria-hidden="true"
            />
            {passees.length === 1 ? '1 semaine déjà passée' : `${passees.length} semaines déjà passées`}
          </button>
          {historiqueOuvert && (
            <ul className="mt-3 space-y-3">
              {passees.map((reg) => (
                <CarteSemaine key={reg.id} reg={reg} onAnnuler={onAnnuler} />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
};

const RegistrationsPage = () => {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingEntry, setProcessingEntry] = useState<string | null>(null);

  const {
    entries: waitingListEntries,
    fetchWaitingList,
    isLoading: isWaitingListLoading,
    removeFromWaitingList,
    markEntryConverted,
  } = useWaitingListStore();

  const { addItem } = useCartStore();
  const { fetchRegistrations } = useRegistrationStore();
  const { clearRegistrationNotification } = useAuthStore();
  const { createRequest, isLoading: isCancellationRequestLoading } = useCancellationRequestStore();

  const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
  const [aAnnuler, setAAnnuler] = useState<Registration | null>(null);

  useEffect(() => {
    fetchDetailedRegistrations();
    fetchWaitingList();
    fetchRegistrations();
    clearRegistrationNotification();
  }, [fetchWaitingList, fetchRegistrations, clearRegistrationNotification]);

  const fetchDetailedRegistrations = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('registrations')
        .select(`
          id,
          amount_paid,
          created_at,
          kid_id,
          activity_id,
          price_type,
          invoice_url,
          cancellation_status,
          kid:kids(prenom, nom),
          session:activity_id(
            stage:stage_id(title, type_activite),
            start_date,
            end_date,
            center:center_id(name)
          ),
          cancellation_request:cancellation_requests(
            id,
            status,
            refund_type,
            credit_note_id,
            credit_note_url
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      /*
        Une inscription dont la session n'est pas lisible reste affichee, avec
        un libelle qui le dit. Sans ce filet, un `null` sur la jointure fait
        lever `reg.session.stage.title` et la page devient blanche — c'est ce
        qui est arrive le 11 septembre 2026 a 184 familles.
      */
      setRegistrations(avecSessionsLisibles(data || []) as unknown as Registration[]);
    } catch (err) {
      console.error('Error fetching registrations:', err);
      setError('Vos inscriptions n’ont pas pu être chargées.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelWaitingList = async (id: string) => {
    try {
      setProcessingEntry(id);
      await removeFromWaitingList(id);
      toast.success('Vous avez été retiré de la liste d’attente');
    } catch (error) {
      console.error('Error removing from waiting list:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setProcessingEntry(null);
    }
  };

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const handleValidateWaitingList = async (entry: any) => {
    if (processingEntry) return; // Prevent double-clicks

    try {
      setProcessingEntry(entry.id);

      // Check if the entry is still valid
      const { data: currentEntry, error: checkError } = await supabase
        .from('waiting_list')
        .select('*')
        .eq('id', entry.id)
        .eq('status', 'invited')
        .single();

      if (checkError || !currentEntry) {
        toast.error("Cette invitation n'est plus valide");
        return;
      }

      // Check if the entry has expired
      if (currentEntry.expires_at && new Date(currentEntry.expires_at) < new Date()) {
        toast.error('Cette invitation a expiré');
        return;
      }

      // Mark the waiting list entry as converted
      await markEntryConverted(entry.id);

      // Get session details for cart
      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select(`
          id,
          stage:stages!stage_id(title, image_url),
          start_date,
          end_date
        `)
        .eq('id', entry.activity_id)
        .single();

      if (sessionError || !sessionData) {
        toast.error("Erreur lors de la récupération des informations de l'activité");
        return;
      }

      const kid = Array.isArray(entry.kid) ? entry.kid[0] : entry.kid;

      /*
        Le panier ne porte plus de prix. Il portait ici un `mainPrice` calcule
        par `getPrice()` et un `sessionStorage` relu plus tard par
        `updatePriceType()` — le chemin exact du defaut qui facturait le second
        enfant au tarif normal. Le tarif est desormais decide par
        `quote_order` a l'affichage et par `create_order` a l'ecriture.
      */
      addItem({
        id: `${entry.activity_id}-${entry.kid_id}`,
        activity_id: entry.activity_id,
        kid_id: entry.kid_id,
        kidName: `${kid.prenom} ${kid.nom}`,
        activityName: sessionData.stage.title,
        activityCategory: 'Stage',
        dateRange: `${new Date(sessionData.start_date).toLocaleDateString('fr-FR')} - ${new Date(sessionData.end_date).toLocaleDateString('fr-FR')}`,
        imageUrl: sessionData.stage.image_url ?? undefined,
      });

      toast.success('Place validée ! Redirection vers le panier...');

      await fetchWaitingList();
      await fetchDetailedRegistrations();

      setTimeout(() => {
        navigate('/cart');
      }, 1500);
    } catch (error) {
      console.error('Error validating waiting list entry:', error);
      toast.error('Une erreur est survenue lors de la validation');
    } finally {
      setProcessingEntry(null);
    }
  };

  /**
   * Ouvre la demande d'annulation, sauf si le stage est termine.
   *
   * Le bouton est deja masque dans ce cas. Le controle est refait ici parce
   * qu'un ecran cache un geste, il ne l'interdit pas : une page restee ouverte
   * pendant que le stage se terminait presenterait encore le bouton.
   *
   * La garantie durable est ailleurs — une regle en base, qui refuserait
   * l'ecriture quelle que soit sa provenance. Elle demande une migration sur
   * une table de l'ancien circuit et reste a faire ; d'ici la, ces deux
   * controles couvrent l'usage reel.
   */
  const handleRequestCancellation = (reg: Registration) => {
    if (estPassee(reg.session.end_date)) {
      toast.error("Ce stage est terminé : l'annulation n'est plus possible.");
      return;
    }
    setAAnnuler(reg);
    setIsCancellationModalOpen(true);
  };

  const handleConfirmCancellation = async (notes: string) => {
    if (!aAnnuler) return;

    try {
      await createRequest(aAnnuler.id, aAnnuler.kid_id, aAnnuler.activity_id, notes);

      setRegistrations((prec) =>
        prec.map((reg) =>
          reg.id === aAnnuler.id ? { ...reg, cancellation_status: 'requested' as const } : reg,
        ),
      );

      toast.success("Votre demande d'annulation a été envoyée");
      setIsCancellationModalOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Une erreur est survenue');
    }
  };

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const texteAttente = (entry: any) => {
    if (entry.status !== 'invited') return 'En attente';
    if (!entry.expires_at) return 'Place disponible';
    const heures = Math.max(
      0,
      Math.floor((new Date(entry.expires_at).getTime() - Date.now()) / (1000 * 60 * 60)),
    );
    return `Place disponible — à confirmer dans ${heures} h`;
  };

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const deplier = (entry: any) => ({
    ...entry,
    kid: Array.isArray(entry.kid) ? entry.kid[0] : entry.kid,
    parent: Array.isArray(entry.parent) ? entry.parent[0] : entry.parent,
    session: Array.isArray(entry.session) ? entry.session[0] : entry.session,
  });

  /*
    Les groupes suivent l'ordre alphabetique des prenoms : il ne change pas
    d'une visite a l'autre, alors que l'ordre des achats, lui, deplacait les
    enfants des qu'une inscription etait ajoutee.
  */
  const groupes = Object.values(
    registrations.reduce<Record<string, { prenom: string; lignes: Registration[] }>>((acc, reg) => {
      const cle = reg.kid_id || 'inconnu';
      if (!acc[cle]) acc[cle] = { prenom: nomEnfant(reg), lignes: [] };
      acc[cle].lignes.push(reg);
      return acc;
    }, {}),
  )
    .map((g) => ({
      prenom: g.prenom,
      aVenir: g.lignes.filter((r) => !estPassee(r.session.end_date)),
      passees: g.lignes.filter((r) => estPassee(r.session.end_date)),
    }))
    .sort((a, b) => a.prenom.localeCompare(b.prenom, 'fr'));

  const attenteActive = waitingListEntries.filter(
    (entry) => entry.status === 'waiting' || entry.status === 'invited',
  );

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:py-10">
      <Link
        to="/dashboard"
        className="mb-4 inline-flex min-h-[44px] items-center text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="mr-2 h-5 w-5" aria-hidden="true" />
        Retour à l’accueil
      </Link>

      <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Mes inscriptions</h1>

      {isLoading ? (
        <div className="flex justify-center py-12" role="status">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" aria-hidden="true" />
          <span className="sr-only">Chargement de vos inscriptions…</span>
        </div>
      ) : error ? (
        <p className="mt-6 rounded-xl bg-red-50 p-4 text-red-800 ring-1 ring-red-200">{error}</p>
      ) : groupes.length === 0 && attenteActive.length === 0 ? (
        <div className="mt-8 rounded-2xl bg-white p-8 text-center ring-1 ring-gray-200">
          <p className="text-gray-600">Vous n’avez pas encore d’inscription.</p>
          <Link to="/activities" className="btn-primary mt-4 inline-flex min-h-[48px] items-center">
            Découvrir les stages
          </Link>
        </div>
      ) : (
        <>
          {groupes.map((g) => (
            <GroupeEnfant
              key={g.prenom}
              prenom={g.prenom}
              aVenir={g.aVenir}
              passees={g.passees}
              onAnnuler={handleRequestCancellation}
            />
          ))}

          {attenteActive.length > 0 && (
            <section className="mt-10">
              <h2 className="mb-3 text-lg font-semibold text-gray-900">Liste d’attente</h2>

              {isWaitingListLoading ? (
                <div className="flex justify-center py-6" role="status">
                  <Loader2 className="h-6 w-6 animate-spin text-primary-600" aria-hidden="true" />
                  <span className="sr-only">Chargement de la liste d’attente…</span>
                </div>
              ) : (
                <ul className="space-y-3">
                  {attenteActive.map((brut) => {
                    const entry = deplier(brut);
                    const invite = entry.status === 'invited';
                    const occupe = processingEntry === entry.id;

                    return (
                      <li
                        key={entry.id}
                        className={clsx(
                          'rounded-xl p-4 ring-1',
                          invite ? 'bg-blue-50 ring-blue-200' : 'bg-white ring-gray-200',
                        )}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <h3 className="min-w-0 text-base font-medium text-gray-900">
                            {entry.session?.stage?.title ?? 'Stage indisponible'}
                          </h3>
                          <span
                            className={clsx(
                              'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset',
                              invite
                                ? 'bg-blue-100 text-blue-800 ring-blue-200'
                                : 'bg-amber-50 text-amber-900 ring-amber-200',
                            )}
                          >
                            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                            {texteAttente(entry)}
                          </span>
                        </div>

                        <p className="mt-1 text-sm text-gray-600">
                          Pour {entry.kid ? `${entry.kid.prenom} ${entry.kid.nom}` : 'un enfant'}
                        </p>
                        {entry.session && (
                          <>
                            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-gray-600">
                              <CalendarDays
                                className="h-4 w-4 flex-shrink-0 text-gray-400"
                                aria-hidden="true"
                              />
                              {periodeLisible(entry.session.start_date, entry.session.end_date)}
                            </p>
                            <p className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-600">
                              <MapPin
                                className="h-4 w-4 flex-shrink-0 text-gray-400"
                                aria-hidden="true"
                              />
                              {entry.session.center?.name}
                            </p>
                          </>
                        )}

                        <div className="mt-3 flex flex-wrap gap-2">
                          {invite && (
                            <button
                              type="button"
                              onClick={() => handleValidateWaitingList(entry)}
                              disabled={occupe}
                              className="btn-primary inline-flex min-h-[44px] items-center disabled:opacity-50"
                            >
                              {occupe ? (
                                <>
                                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                                  Validation…
                                </>
                              ) : (
                                'Valider la place'
                              )}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleCancelWaitingList(entry.id)}
                            disabled={occupe}
                            className="inline-flex min-h-[44px] items-center rounded-lg border border-red-200 px-3 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                          >
                            {occupe ? 'Retrait…' : 'Me retirer de la liste'}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}
        </>
      )}

      <CancellationRequestModal
        isOpen={isCancellationModalOpen}
        onClose={() => setIsCancellationModalOpen(false)}
        onConfirm={handleConfirmCancellation}
        registrationDetails={aAnnuler}
        isLoading={isCancellationRequestLoading}
      />

      <Toaster position="top-right" />
    </div>
  );
};

export default RegistrationsPage;
